import React, { useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { Plus, Trash2, ClipboardList } from 'lucide-react';
import { Modal, Button } from '../common';
import { Category, TestQuestionMode, TestAnswerOption, CreateTestQuestionData } from '../../types';

interface QuestionFormData {
  categoryId: string;
  question: string;
  mode: TestQuestionMode;
  points: number;
  answers: { text: string }[];
  correctSingle?: string;
  correctMulti: Record<string, boolean>;
}

interface CreateTestQuestionModalProps {
  isOpen: boolean;
  onClose: () => void;
  categories: Category[];
  onSubmit: (data: CreateTestQuestionData) => Promise<void>;
  isSubmitting: boolean;
}

export const CreateTestQuestionModal: React.FC<CreateTestQuestionModalProps> = ({
  isOpen,
  onClose,
  categories,
  onSubmit,
  isSubmitting,
}) => {
  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
    reset,
  } = useForm<QuestionFormData>({
    defaultValues: {
      mode: 'single',
      points: 1,
      answers: [{ text: '' }, { text: '' }],
      correctMulti: {},
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'answers' });
  const mode = watch('mode');

  const onSubmitForm = async (data: QuestionFormData) => {
    const answers: TestAnswerOption[] = data.answers.map((a, i) => ({
      id: String.fromCharCode(97 + i), // a, b, c, d...
      text: a.text.trim(),
    }));

    let correctAnswer: string | string[];
    if (data.mode === 'single') {
      correctAnswer = data.correctSingle || answers[0].id;
    } else {
      correctAnswer = answers
        .map((_, i) => String.fromCharCode(97 + i))
        .filter((id) => data.correctMulti[id]);
      if (correctAnswer.length === 0) {
        alert('Selecciona al menos una respuesta correcta.');
        return;
      }
    }

    await onSubmit({
      categoryId: data.categoryId,
      question: data.question.trim(),
      mode: data.mode,
      answers,
      correctAnswer,
      points: data.points ?? 1,
    });

    reset();
    onClose();
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Nueva Pregunta de Test" size="lg">
      <form onSubmit={handleSubmit(onSubmitForm)} className="space-y-5">

        {/* Categoría */}
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">Categoría *</label>
          <select
            {...register('categoryId', { required: 'La categoría es requerida' })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brinsa-azul"
          >
            <option value="">Seleccionar categoría...</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          {errors.categoryId && <p className="mt-1 text-sm text-red-600">{errors.categoryId.message}</p>}
        </div>

        {/* Enunciado */}
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">Enunciado *</label>
          <textarea
            {...register('question', { required: 'El enunciado es requerido' })}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brinsa-azul resize-none"
            placeholder="¿Cuál es la función principal de...?"
          />
          {errors.question && <p className="mt-1 text-sm text-red-600">{errors.question.message}</p>}
        </div>

        {/* Tipo + Puntos */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">Tipo de respuesta *</label>
            <select
              {...register('mode')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brinsa-azul"
            >
              <option value="single">Selección única</option>
              <option value="multi">Selección múltiple</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">Puntos</label>
            <input
              type="number"
              min={1}
              max={10}
              {...register('points', { valueAsNumber: true, min: 1 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brinsa-azul"
            />
          </div>
        </div>

        {/* Opciones de respuesta */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-900">
              Opciones de respuesta *
              <span className="ml-1 text-xs text-gray-800/60 font-normal">
                ({mode === 'single' ? 'marca la correcta con el radio' : 'marca una o varias correctas'})
              </span>
            </label>
            {fields.length < 6 && (
              <button
                type="button"
                onClick={() => append({ text: '' })}
                className="flex items-center gap-1 text-xs text-gray-900 hover:underline"
              >
                <Plus className="w-3 h-3" /> Agregar opción
              </button>
            )}
          </div>

          <div className="space-y-2">
            {fields.map((field, index) => {
              const optionId = String.fromCharCode(97 + index);
              return (
                <div key={field.id} className="flex items-center gap-3">
                  {/* Selector de correcta */}
                  {mode === 'single' ? (
                    <input
                      type="radio"
                      value={optionId}
                      {...register('correctSingle', { required: 'Selecciona la respuesta correcta' })}
                      className="w-4 h-4 text-brinsa-azul border-gray-300 flex-shrink-0"
                      title="Respuesta correcta"
                    />
                  ) : (
                    <input
                      type="checkbox"
                      {...register(`correctMulti.${optionId}`)}
                      className="w-4 h-4 text-brinsa-azul border-gray-300 rounded flex-shrink-0"
                      title="Respuesta correcta"
                    />
                  )}

                  {/* Letra */}
                  <span className="w-5 text-xs font-semibold text-gray-800/70 flex-shrink-0 uppercase">
                    {optionId})
                  </span>

                  {/* Texto */}
                  <input
                    type="text"
                    {...register(`answers.${index}.text`, { required: 'El texto es requerido' })}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brinsa-azul text-sm"
                    placeholder={`Opción ${optionId.toUpperCase()}`}
                  />

                  {/* Eliminar */}
                  {fields.length > 2 && (
                    <button
                      type="button"
                      onClick={() => remove(index)}
                      className="p-1.5 text-gray-800/50 hover:text-red-500 transition-colors flex-shrink-0"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {mode === 'single' && errors.correctSingle && (
            <p className="mt-1 text-sm text-red-600">{errors.correctSingle.message}</p>
          )}
        </div>

        {/* Botones */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100">
          <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button type="submit" isLoading={isSubmitting} leftIcon={<ClipboardList className="w-4 h-4" />}>
            Crear Pregunta
          </Button>
        </div>
      </form>
    </Modal>
  );
};
